import { Transaction } from "./Transactions";
import { Company, CompanyRole } from "./CompanyInfo";
import { ArticlesOfAssociation } from "./ArticlesOfAssociation";
import { FileUploadConfig } from "../fileUpload/fileUploadDomain";
import { TMessage } from "../i18n/tt";
import { DocumentType } from "../common/documents";
import { isCountryCodeNorway, isCountryCodeSweden } from "../utils/countries";
import {
  getShareSumByType,
  getShareSumWithoutCompany,
  ShareholderCommitment,
  ShareholderContribution,
} from "../shareholderRegister/shareholderRegisterDomain";
import {
  Address,
  CompanyShareholderContact,
  FairValue,
  PhoneNumber,
  ShareValuationInformation,
} from "../common/common";
import { ShareType, ShareTypeCode } from "../common/share";
import { MessageForRecipient } from "../messaging/recipientMessagingDomain";
import { MeetingListItem as MeetingListItemFull } from "../meetings/meetingsDomain";
import { ShareholderFollowUpItem } from "../portfolio/followUp/shareholdersFollowUpDomain";
import { ValidationError } from "../utils/Errors";

export type ExtendedShareholderDTO = ShareholderDTO & {
  calculatedShares: { [key in ShareTypeCode]?: number };
  calculatedVotes: { [key in ShareTypeCode]?: number };
  naturalId?: string;
};

export function emptyShareholder(
  data: Partial<ExtendedShareholderDTO> = {},
): ExtendedShareholderDTO {
  return {
    organizationNumber: data.organizationNumber ?? "123",
    address: data.address ?? {},
    data: data.data ?? [],
    email: data.email ?? "",
    id: data?.id ?? 0,
    name: data?.name ?? "",
    isCompany: data.isCompany ?? false,
    calculatedShares: data.calculatedShares ?? calculateShares(data.data ?? []),
    calculatedVotes: data.calculatedVotes ?? calculateVotes(data.data ?? []),
    ...data,
  };
}

export type MeetingListItem = {
  documentId: string;
  documentType: DocumentType;
  meetingDate?: string;
  downloadUrl: string;
};

export type ShareIntervalInformation = {
  id: number;
  numberOfShares: number;
  from: number;
  to: number;
  costPrice: number;
  costPricePerShare: number;
  sharePremium: number;
  nominalValue: number;
  transactionDate: string;
};

export type ShareholdingInformationPerShareClass = {
  shareTypeId: number;
  shareTypeCode: string;
  numberOfShares: number;
  numberOfVotes: number;
  totalPaidInCapital: number;
  ownership?: number;
  totalNumberOfSharesInCompany: number;
  shareIntervals: ShareIntervalInformation[];
};

export type ShareInformation = {
  totalPaid: number;
  totalShares: number;
  totalOwnership: number;
  totalNumberOfSharesInCompany: number;
  shareholding: ShareholdingInformationPerShareClass[];
  issuedShareholderCertificate: boolean;
  firstTransactionDate?: string;
  lastTransactionDate?: string;
  nominalValue: number;
  currency: string;
};

export interface ShareholderViewResponse {
  articlesOfAssociation?: ArticlesOfAssociation;
  company: Company;
  fairValues: FairValue[];
  isRepresentative: boolean;
  meetings: MeetingListItem[];
  representative?: PersonInfo;
  roles: CompanyRole[];
  shareholder: ShareholderDetailsResponseDTO;
  shareTypes: ShareType[];
  shareholderId: number;
  messages: MessageForRecipient[];
  currentMeeting?: MeetingListItemFull;
  logoBase64?: string;
  shareholderContact?: CompanyShareholderContact;
}

export interface ShareholdersInfo {
  shareholders: Array<ExtendedShareholderDTO>;
  shareTypes: Array<ShareType>;
  shareTypeCodes: Array<string>;
  calculatedSharesBySharetypeCode: {
    [shareTypeCode: string]: number;
  };
  calculatedVotesBySharetypeCode: {
    [shareTypeCode: string]: number;
  };
  followUps: ShareholderFollowUpItem[];
  importExcelConfig: FileUploadConfig;
}

export interface PersonInfo {
  personInfoId?: number;
  phoneNumber?: PhoneNumber;
  socialSecurityNumber?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  email?: string;
  lastLogin?: string;
  birthDate?: string;
  gender?: Gender;
}

export const formatName = ({ firstName, middleName, lastName }: PersonInfo): string =>
  [firstName, middleName, lastName].join(" ");

export type Gender = "male" | "female";

const genderLabels: { [key in Gender]: TMessage } = {
  ["male"]: { key: "gender.male" },
  ["female"]: { key: "gender.female" },
};

// The non-breaking space is used to ensure that select options have full height.
export function getGenderLabel(gender?: Gender | null): TMessage {
  return gender ? genderLabels[gender] : { translated: " " };
}

export interface AddressInfo {
  addressId?: number;
  postalCode?: string;
  city?: string;
  country: string;
  address?: string;
}

export type SummaryValue = {
  total: number;
  avg?: number;
  count: number;
};

export type ShareholderTransactionSummary = {
  acquisitions: SummaryValue;
  sales: SummaryValue;
  dividends: SummaryValue;
  hasFullHistory: boolean;
  firstRealTransactionDate?: string;
};

export interface ShareholderDetailsResponseDTO {
  shareholderId: number;
  shareholderName: string;
  isCompany: boolean;
  organizationnumber: string;
  personInfo?: PersonInfo;
  companyNumber?: string;
  companyName?: string;
  shareholderAddress?: AddressInfo;
  contactInfo?: PersonInfo;
  bankDetailsCountry?: string;
  bic?: string;
  iban?: string;
  birthDateOrOrganisationNumber?: string;
  portfolio: ShareholderPortfolio[];
  transactionLog: Array<Transaction>;
  isPledged: boolean;
  pledgeComment: string;
  lockupEndDate?: string;
  loanAmount?: number;
  nationality?: string;
  issuedShareholderCertificate: boolean;
  utlNumber?: string;
  isEmployee?: boolean;
  usernames: ShareholderUsername[];
  shareholderContributions?: ShareholderContribution[];
  lastLogin?: string;
  additionalRepresentatives?: PersonInfo[];
  shareInformation: ShareInformation;
  taxAssessmentInformation: ShareValuationInformation[];
  fairValueInformation: ShareValuationInformation[];
  transactionSummary: ShareholderTransactionSummary;
  commitments: ShareholderCommitment[];
  accessibleShareholderDetails: AccessibleShareholderDetail[];
  remark?: string;
  hasIncompleteCostPriceHistory?: boolean;
  overrideCostPriceDisplay?: boolean;
}

export type ShareholderUsername = {
  username: string;
  hasPassword: boolean;
};

export type AccessibleShareholderDetail = {
  shareholderId: number;
  companyName: string;
  organisationNumber: string;
};

export interface SharesForClass {
  shareTypeId: number;
  shareTypeCode: number;
  numberOfShares: number;
}

export interface ShareholderShares {
  shareTypeId: number;
  vote?: number;
  numberOfShares: number;
  numberOfVotes: number;
  sharetypeCode: ShareTypeCode;
}

export interface GetShareholdersResponse {
  shareholders: ShareholderDTO[];
  shareTypes: ShareType[];
  shareTypeCodes: string[];
  calculatedSharesBySharetypeCode: {
    [shareTypeCode: string]: number;
  };
  calculatedVotesBySharetypeCode: {
    [shareTypeCode: string]: number;
  };
  followUps: ShareholderFollowUpItem[];
  importExcelConfig: FileUploadConfig;
}

export type ContactInfo = PersonInfo;

export interface ShareholderDTO {
  id: number;
  organizationNumber: string;
  isCompany: boolean;
  name: string;
  address?: Address;
  email: string;
  birthDateOrOrganisationNumber?: string;
  data: ShareholderShares[];
  companyName?: string;
  companyNumber?: string;
  personInfo?: PersonInfo;
  contactInfo?: ContactInfo;
  utlNumber?: string;
  lockupEndDate?: string;
  loanAmount?: number;
  bic?: string;
  iban?: string;
  bankDetailsCountry?: string;
  createdAt?: string;
  canReceiveInvitationEmail?: boolean;
  nationality?: string;
  issuedShareholderCertificate?: boolean;
  isEmployee?: boolean;
  lastLogin?: string;
  representatives?: PersonInfo[];
  naturalId?: string;
}

export interface UpdateShareholderDTO {
  shareholderIds: number[];
  organizationnumber: string;
  personInfo?: PersonInfo;
  companyNumber?: string;
  companyName?: string;
  shareholderAddress: Address;
  isCompany: boolean;
  bic?: string;
  iban?: string;
  bankDetailsCountry?: string;
  contactInfo?: PersonInfo;
  utlNumber?: string;
  nationality?: string;
  isEmployee?: boolean;
  issuedShareholderCertificate: boolean;
}

export interface PortfolioShares {
  shareNumbers: string;
  sharePrice?: number;
  sharePremium: number;
  acquisitionDate: string;
}

export interface ShareholderPortfolio {
  shareTypeName: string;
  sharetypeId: number;
  numberOfShares: number;
  shareTypeCode: string;
  shares: Array<PortfolioShares>;
}

export interface GenerellInformasjon {
  organisationNumber: string;
  shareType: ShareType;
  year: number;
}

export interface SelskapsOpplysninger {
  nominalValue: number;
  shareCapital: number;
  shareCapitalForShareType: number;
  numberOfSharesForShareType: number;
  totalSharePriceForShareType: number;
  totalSharePremiumForShareType: number;
  nominalValueLastYear: number;
  shareCapitalLastYear: number;
  shareCapitalForShareTypeLastYear: number;
  numberOfSharesForShareTypeLastYear: number;
  totalSharePriceForShareTypeLastYear: number;
  totalSharePremiumForShareTypeLastYear: number;
}

export interface UtdeltSkatterettsligUtbytteILopetAvInntektsaret {
  totalDividendForShareType: number;
  dividendForPerShare: number;
  dividendDateTime: string;
}

export interface Utbytte {
  children: UtdeltSkatterettsligUtbytteILopetAvInntektsaret[];
}

export interface AntallNyutstedteAksjer {
  antallAksjerUtstedt: number;
  antallAksjerEtter: number;
  hendelsestype: string;
  tidspunkt: string;
  paalydende: number;
  innbetaltOverkursPerAksje: number;
}

export interface UtstedelseAvAksjerIfmStiftelseNyemisjon {
  children: AntallNyutstedteAksjer[];
}

export interface NyutstedteAksjerOmfordeling {
  aksjerNyutstedteFondsemisjonMvAntall: number;
  aksjerNyutstedteFondsemisjonMvAntallEtter: number;
  hendelsestype: string;
  tidspunkt: string;
  paalydende: number;
}

export interface UtstedelseAvAksjerIfmFondsemisjonSplittMv {
  children: NyutstedteAksjerOmfordeling[];
}

export interface NedsettelseAvInnbetaltOverkursMedTilbakebetalingTilAksjonarene {
  amount: number;
  dateTime: string;
}

export interface ForhoyelseAvAKVedOkningAvPalydende3462 {
  shareCapitalIncrease: number;
  nominalValueIncrease: number;
  nominalValueAfter: number;
  dateTime: string;
}

export interface ForhoyelseAvAKVedOkningAvPalydende3463 {
  shareCapitalIncrease: number;
  nominalValueIncrease: number;
  nominalValueAfterIncrease: number;
  dateTime: string;
  sharePremiumIncrease: number;
  eventType: AksjekapitalForhoyelsePalydendeHendelsestype28268;
  transferringCompanyOrgNumber: string;
  transferringCompanyShareType: ShareTypeCode;
}

export type AksjekapitalForhoyelsePalydendeHendelsestype28268 =
  | "NYEMISJON"
  | "KONVERTERING_FORDRING"
  | "FORENKLET_FUSJON";

export interface NedsettelseAvInnbetaltOgFondsemittertAK3464 {
  shareCapitalReductionPaidInAmount: number;
  nominalValueReductionAmount: number;
  nominalValueAfterReduction: number;
  dateTime: string;
}

export interface NedsettelseAKVedReduksjonAvPalydende3465 {
  shareCapitalReductionPaidInAmount: number;
  nominalValueReductionAmount: number;
  nominalValueAfterReduction: number;
  dateTime: string;
}

export interface EndringerIAksjekapitalOgOverkurs3460 {
  sharePremiumDecreases: NedsettelseAvInnbetaltOverkursMedTilbakebetalingTilAksjonarene[];
  nominalValueBonusIssueIncreases: ForhoyelseAvAKVedOkningAvPalydende3462[];
  nominalValueIncreases: ForhoyelseAvAKVedOkningAvPalydende3463[];
  nominalValueDecreases: NedsettelseAvInnbetaltOgFondsemittertAK3464[];
  shareCapitalDecreasesByDistribution: NedsettelseAKVedReduksjonAvPalydende3465[];
}

export interface RF1086Skjema {
  generellInformasjon: GenerellInformasjon;
  selskapsOpplysninger: SelskapsOpplysninger;
  utbytte: Utbytte;
  utstedelseStiftelseNyEmisjon: UtstedelseAvAksjerIfmStiftelseNyemisjon;
  utstedelseFondsemisjonSplitt: UtstedelseAvAksjerIfmFondsemisjonSplittMv;
  endringerIAksjekapitalOgOverkurs3460: EndringerIAksjekapitalOgOverkurs3460;
  slettingLikvidasjon: { children: SlettedeAksjerAvgang3457[] | undefined };
  slettingSpleis: { children: SlettedeAksjerOmfordelinggrp3459[] | undefined };
}

export interface SlettedeAksjerAvgang3457 {
  slettedeAksjer: number;
  antallAksjerEtter: number;
  nominalValue: number;
  hendelsestype: string;
  tidspunkt: string;
  innbetaltOverkursGjennomsnittligPerAksje: number;
  totaltVederlag: number;
}

export interface SlettedeAksjerOmfordelinggrp3459 {
  numberOfCancelledShares: number;
  numberOfSharesAfter: number;
  transactionType: string;
  tidspunkt: string;
  nominalValueAfter: number;
}

export interface Selskapsidentifikasjon {
  organisationNumber: string;
  shareType: ShareType;
  year: number;
}

export interface NorskUtenlandskAksjonar {
  shareholderId: number;
  isCompany: boolean;
  personInfo?: PersonInfo;
  companyNumber?: string;
  name?: string;
  shareholderAddress?: Address;
}

export interface SelskapsOgAksjonaropplysninger {
  selskapsidentifikasjon: Selskapsidentifikasjon;
  norskUtenlandskAksjonar: NorskUtenlandskAksjonar;
}

export interface AntallAksjerPerAksjonar {
  numberOfSharesThisYear: number;
  numberOfSharesLastYear: number;
}

export interface UtdeltUtbyttePerAksjonar {
  amount: number;
  numberOfShares: number;
  dateTime: string;
}

export interface UtdeltUtbytteKildeskatt {
  country: string;
}

export interface AntallAksjerUtbytteOgTilbakebetaling {
  antallAksjerPerAksjonar: AntallAksjerPerAksjonar;
  utdeltUtbyttePerAksjonar: UtdeltUtbyttePerAksjonar[];
  utdeltUtbytteKildeskatt: UtdeltUtbytteKildeskatt;
}

export interface AksjerIAvgang {
  aksjerArvMvOmsattAntall: number;
  aksjerArvMvOmsattType: string;
  tidspunkt: string;
  totaltVederlag: number;
}

export interface AntallAksjerIAvgangVedOmfordelinggrp4003 {
  antallAksjerIAvgang: number;
  hendelsestype: string;
  tidspunkt: string;
}

export interface AntallAksjerITilgang {
  aksjerKjoptAntall: number;
  transaksjonstype: string;
  tidspunkt: string;
  totalAnskaffelsesVerdi: number;
}

export interface AntallAksjerITilgangIfmOmfordeling {
  antallAksjerITilgang: number;
  aksjerTilgangFondsemisjonMvType: string;
  tidspunkt: string;
}

export interface RF1086USkjema {
  selskapsOgAksjonaropplysninger: SelskapsOgAksjonaropplysninger;
  antallAksjerUtbytteOgTilbakebetaling: AntallAksjerUtbytteOgTilbakebetaling;
  transaksjoner: { kjopArvGaveNyemisjon?: { children: AntallAksjerITilgang[] } };
  salg: { children: AksjerIAvgang[] };
  fondsemisjonSplittMv: { children: AntallAksjerITilgangIfmOmfordeling[] };
  endringerIAksjekapitalOgOverkurs: EndringerIAksjekapitalOgOverkurs;
  spleis: { children: AntallAksjerIAvgangVedOmfordelinggrp4003[] };
}

export interface TilbakebetaltInnbetaltOgFondsemittertAKVedReduksjonAvPalydende4000 {
  shareCapitalDecreaseAmount: number;
  nominalValueDecreaseAmount: number;
  dateTime: string;
}

export interface TilbakebetaltTidligereInnbetaltOverkursForAksjen {
  amount: number;
  dateTime: string;
}

export interface ForhoyelseAvInnbetaltAksjekapitalVedOkning {
  shareCapitalIncrease: number;
  sharePremiumIncrease: number;
  nominalValueIncrease: number;
  eventType: AksjekapitalNyemisjonForhoyelsePalydendeTransaksjonstype28267;
  dateTime: string;
}

export type AksjekapitalNyemisjonForhoyelsePalydendeTransaksjonstype28267 =
  | "NYEMISJON"
  | "KONVERTERING_FORDRING"
  | "FORENKLET_FUSJON";

export interface EndringerIAksjekapitalOgOverkurs {
  tilbakebetaltInnbetaltOgFondsemittertAKVedReduksjonAvPalydendeList: TilbakebetaltInnbetaltOgFondsemittertAKVedReduksjonAvPalydende4000[];
  tilbakebetaltTidligereInnbetaltOverkursForAksjenList: TilbakebetaltTidligereInnbetaltOverkursForAksjen[];
  forhoyelseAvInnbetaltAksjekapitalVedOkningList: ForhoyelseAvInnbetaltAksjekapitalVedOkning[];
}

export interface AltinnShareholderRegisterFormData {
  mainForm: RF1086Skjema;
  subForms: RF1086USkjema[];
}

export interface AltinnShareholderRegisterFormDataPerSharetype {
  formData: AltinnShareholderRegisterFormData;
  sharetype: ShareType;
}

export const calculateShares = (shares: ShareholderShares[]): { [key in ShareTypeCode]?: number } =>
  shares.reduce<{ [key in ShareTypeCode]?: number }>(
    (acc, cur) => ({
      ...acc,
      [cur.sharetypeCode]: cur.numberOfShares,
    }),
    {},
  );

export const calculateVotes = (shares: ShareholderShares[]): { [key in ShareTypeCode]?: number } =>
  shares.reduce<{ [key in ShareTypeCode]?: number }>(
    (acc, cur) => ({
      ...acc,
      [cur.sharetypeCode]: cur.numberOfVotes,
    }),
    {},
  );

export const transformShareholderInfo = (fetched: GetShareholdersResponse): ShareholdersInfo => ({
  ...fetched,
  shareholders: fetched.shareholders.map((s) => transformShareholder(s)),
});

export const transformShareholder = (fetched: ShareholderDTO): ExtendedShareholderDTO => ({
  ...fetched,
  calculatedShares: calculateShares(fetched.data),
  calculatedVotes: calculateVotes(fetched.data),
});

export type ShareholderData = {
  shareholders: ExtendedShareholderDTO[];
  shareTypes: ShareType[];
  shareTypeCodes: string[];
  shareSumByType: Record<string, number>;
  shareSumWithoutCompany: Record<string, number>;
};

export const prepShareholderData = (
  orgNo: string,
  shareholdersResponse: GetShareholdersResponse,
): ShareholderData => {
  const { shareholders, shareTypes, shareTypeCodes } =
    transformShareholderInfo(shareholdersResponse);
  const shareSumByType = getShareSumByType(shareholders);
  return {
    shareholders: shareholders,
    shareTypes: shareTypes,
    shareTypeCodes: shareTypeCodes,
    shareSumByType: shareSumByType,
    shareSumWithoutCompany: getShareSumWithoutCompany(orgNo, shareholders, shareSumByType),
  };
};

export type NewShareholderResponse = {
  shareholderId: number;
};

export enum ShareholderType {
  person = "person",
  company = "company",
}

export enum ShareholderField {
  shareholderName = "shareholderName",
  contactInfoName = "contactInfoName",
  contactInfoNationalId = "contactInfoNationalId",
  email = "email",
  phone = "phone",
  nationalId = "nationalId",
  nationality = "nationality",
  companyNumber = "companyNumber",
  address = "address",
  postalCode = "postalCode",
  city = "city",
  country = "country",
  utlNumber = "utlNumber",
  lockupEndDate = "lockupEndDate",
  loanAmount = "loanAmount",
  issuedShareholderCertificate = "issuedShareholderCertificate",
  bankDetailsCountry = "bankDetailsCountry",
  bic = "bic",
  iban = "iban",
  gender = "gender",
  isEmployee = "isEmployee",
}

export type UpdateShareholderResponse = {
  shareholderId: number;
  name?: string;
};

export type UpdateShareholderFieldResponse = {
  validationError: ValidationError;
  shareholderId: number;
  success: boolean;
};

export type CompanySubsidiary = {
  organizationNumber: string;
  name: string;
  ownershipInPercent?: number;
  ownershipType: "Direct" | "Indirect" | "Unknown";
  level: number;
};

export function numberOfSharesForShareType(
  shareholder: ExtendedShareholderDTO,
  shareTypeCode: ShareTypeCode,
) {
  return shareholder.calculatedShares[shareTypeCode] ?? 0;
}

export function totalNumberOfSharesOfShareholder(shareholder: ExtendedShareholderDTO): number {
  return totalNumberOfShares(shareholder.calculatedShares);
}

export function totalNumberOfVotesForShareholder(shareholder: ExtendedShareholderDTO): number {
  return isOwnShareholder(shareholder) ? 0 : totalNumberOfVotes(shareholder.calculatedVotes);
}

export function isOwnShareholder(s: ExtendedShareholderDTO) {
  return s.companyNumber === s.organizationNumber;
}

export function totalNumberOfSharesOfShareholders(shareholders: ExtendedShareholderDTO[]): number {
  return shareholders.reduce(
    (total, shareholder) => total + totalNumberOfSharesOfShareholder(shareholder),
    0,
  );
}

export function totalNumberOfShares(shares: { [s in ShareTypeCode]?: number }) {
  return Object.entries(shares).reduce(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (total, [_, numberOfShares]) => total + numberOfShares,
    0,
  );
}

export function totalNumberOfVotes(shares: { [s in ShareTypeCode]?: number }) {
  return Object.entries(shares).reduce((total, entry) => total + entry[1], 0);
}

export function formatShareholderName(s: ExtendedShareholderDTO) {
  if (s.birthDateOrOrganisationNumber) {
    return `${s.name} (${s.birthDateOrOrganisationNumber})`;
  } else {
    return s.name;
  }
}

export function isMissingSocialSecurityNumber(
  s: ExtendedShareholderDTO | ShareholderDetailsResponseDTO,
): boolean {
  return (
    (isCountryCodeNorway(s.nationality) || isCountryCodeSweden(s.nationality)) &&
    (s.isCompany ? !s.contactInfo?.socialSecurityNumber : !s.personInfo?.socialSecurityNumber)
  );
}

export function getShareTypeVote(shareTypeId: number, shareTypes: ShareType[]) {
  return shareTypes.find((t) => t.sharetypeId === shareTypeId)?.vote ?? 0;
}

export function toShareholderDTO(sv: ShareholderViewResponse): ExtendedShareholderDTO {
  return emptyShareholder({
    address: sv.shareholder.shareholderAddress,
    bankDetailsCountry: sv.shareholder.bankDetailsCountry,
    bic: sv.shareholder.bic,
    birthDateOrOrganisationNumber: sv.shareholder.birthDateOrOrganisationNumber,
    companyName: sv.shareholder.companyName,
    companyNumber: sv.shareholder.companyNumber,
    contactInfo: sv.shareholder.contactInfo,
    data: sv.shareholder.shareInformation.shareholding.map((sh) => ({
      numberOfShares: sh.numberOfShares,
      numberOfVotes: sh.numberOfVotes,
      sharetypeCode: sh.shareTypeCode as ShareTypeCode,
      shareTypeId: sh.shareTypeId,
      vote: sv.shareTypes.find((st) => st.sharetypeId === sh.shareTypeId)?.vote,
    })),
    email:
      (sv.shareholder.isCompany
        ? sv.shareholder.contactInfo?.email
        : sv.shareholder.personInfo?.email) ?? "",
    id: sv.shareholderId,
    isCompany: sv.shareholder.isCompany,
    iban: sv.shareholder.iban,
    isEmployee: sv.shareholder.isEmployee,
    issuedShareholderCertificate: sv.shareholder.issuedShareholderCertificate,
    lastLogin: sv.shareholder.lastLogin,
    loanAmount: sv.shareholder.loanAmount,
    lockupEndDate: sv.shareholder.lockupEndDate,
    name: sv.shareholder.shareholderName,
    nationality: sv.shareholder.nationality,
    naturalId: sv.shareholder.nationality,
    organizationNumber: sv.shareholder.organizationnumber,
    personInfo: sv.shareholder.personInfo,
    representatives: sv.shareholder.additionalRepresentatives,
    utlNumber: sv.shareholder.utlNumber,
  });
}
